import React, { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useFarm } from "@/lib/farmContext";
import { usePredictedBirths } from "@/lib/repo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Baby, Download, AlertTriangle } from "lucide-react";
import { daysUntil, monthLabel, formatDate } from "@/lib/utils";

export default function PredictedBirths() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { farms } = useFarm();
  const [farmId, setFarmId] = useState("all");
  const [days, setDays] = useState("all");
  const [view, setView] = useState("pregnant");

  const assignedFarmIds = (user?.role === "handler" || user?.role === "vet") ? (user?.assigned_farm_ids || []) : undefined;
  const commonFilters = {
    assignedFarmIds,
    farmId: farmId !== "all" ? farmId : undefined,
    view,
  };
  const items = usePredictedBirths({ ...commonFilters, days: days !== "all" ? Number(days) : undefined });
  const upcoming90 = usePredictedBirths({ assignedFarmIds, view: "pregnant", days: 90 });

  const grouped = useMemo(() => {
    if (view === "empty") return null;
    const map = {};
    items.forEach((i) => {
      const key = i.due_date ? i.due_date.slice(0, 7) : "sin-fecha";
      if (!map[key]) map[key] = { label: monthLabel(i.due_date), items: [] };
      map[key].items.push(i);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [items, view]);

  const farmName = (id) => farms.find((f) => f.id === id)?.name || "—";

  const exportCsv = () => {
    const header = view === "empty"
      ? ["Chip", "Lomo", "Tatuaje", "Finca", "Estado", "Fecha palpación", "Registrado por"]
      : ["Chip", "Lomo", "Tatuaje", "Finca", "Meses de preñez", "Fecha palpación", "Fecha estimada de parto", "Días restantes", "Veterinario", "Registrado por"];
    const rows = items.map((i) => view === "empty"
      ? [i.animal.chip_code || "", i.animal.lomo_number || "", i.animal.ear_tattoo || "", farmName(i.animal.farm_id),
         i.diagnosis === "empty_cull" ? "Vacía descarte" : "Vacía", i.palpation_date || "", i.registered_by_name || ""]
      : [i.animal.chip_code || "", i.animal.lomo_number || "", i.animal.ear_tattoo || "", farmName(i.animal.farm_id),
         i.months ?? "", i.palpation_date || "", i.due_date || "",
         i.due_date ? daysUntil(i.due_date) : "", i.vet || "", i.registered_by_name || ""]
    );
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `nacimientos-previstos-${view}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="predicted-births-page" className="space-y-4 max-w-3xl mx-auto">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9"><ArrowLeft size={18} /></Button>
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">Reporte de palpación</div>
          <h1 className="font-editorial text-[26px] sm:text-[32px] leading-[1.05] tracking-tight">Nacimientos previstos</h1>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={items.length === 0} data-testid="predicted-births-export">
          <Download size={13} className="mr-1" />CSV
        </Button>
      </div>

      <Card data-testid="predicted-births-counter">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[hsl(var(--brand-ochre)/0.16)] text-[hsl(var(--brand-ochre))] grid place-items-center shrink-0">
            <Baby size={20} />
          </div>
          <p className="text-[15px]">
            <span className="font-editorial text-[22px] tabular-nums mr-1">{upcoming90.length}</span>
            nacimiento{upcoming90.length === 1 ? "" : "s"} previsto{upcoming90.length === 1 ? "" : "s"} en los próximos 90 días
          </p>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-full border border-border bg-card p-0.5" data-testid="predicted-births-view-toggle">
          <button
            data-testid="predicted-births-view-pregnant"
            onClick={() => setView("pregnant")}
            className={`px-3 h-8 rounded-full text-[12.5px] font-medium transition-colors ${view === "pregnant" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
          >Preñadas</button>
          <button
            data-testid="predicted-births-view-empty"
            onClick={() => setView("empty")}
            className={`px-3 h-8 rounded-full text-[12.5px] font-medium transition-colors ${view === "empty" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
          >Vacías</button>
        </div>

        <Select value={farmId} onValueChange={setFarmId}>
          <SelectTrigger className="h-9 w-auto min-w-[140px]" data-testid="predicted-births-farm-filter"><SelectValue placeholder="Finca" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las fincas</SelectItem>
            {farms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>

        {view === "pregnant" && (
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="h-9 w-auto min-w-[140px]" data-testid="predicted-births-days-filter"><SelectValue placeholder="Período" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Próximos 30 días</SelectItem>
              <SelectItem value="60">Próximos 60 días</SelectItem>
              <SelectItem value="90">Próximos 90 días</SelectItem>
              <SelectItem value="all">Todas</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {items.length === 0 && (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
          {view === "empty" ? "No hay vacas diagnosticadas vacías en este filtro." : "No hay nacimientos previstos en este filtro."}
        </CardContent></Card>
      )}

      {view === "pregnant" && grouped && grouped.map(([key, group]) => (
        <div key={key} className="space-y-2">
          <div className="flex items-center gap-2 px-1">
            <h2 className="text-[13px] font-heading uppercase tracking-[0.08em] text-muted-foreground">{group.label}</h2>
            <Badge variant="muted" className="text-[10px]">{group.items.length}</Badge>
          </div>
          <Card>
            <CardContent className="p-0 divide-y divide-border">
              {group.items.map((i) => {
                const remaining = i.due_date ? daysUntil(i.due_date) : null;
                const overdue = remaining !== null && remaining < 0;
                return (
                  <Link key={i.id} to={`/animales/${i.animal.id}`} data-testid={`predicted-birth-row-${i.animal.id}`} className="p-3 flex items-center gap-3 text-sm hover:bg-accent/30 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium flex items-center gap-1.5 flex-wrap">
                        Lomo {i.animal.lomo_number || "—"}
                        {i.animal.chip_code && <span className="font-mono text-[11px] text-muted-foreground">#{i.animal.chip_code.slice(-6)}</span>}
                        {overdue && <Badge variant="destructive" className="text-[10px] px-1.5 py-0" data-testid={`predicted-birth-overdue-${i.animal.id}`}><AlertTriangle size={10} className="mr-0.5" />Vencida</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {farmName(i.animal.farm_id)} · {i.months ? `${i.months} meses de preñez` : "Preñada"} · Palpada {formatDate(i.palpation_date)}
                        {i.vet && <> · Dr(a). {i.vet}</>}
                        {i.registered_by_name && <> · por {i.registered_by_name}</>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-editorial text-[18px] tabular-nums">{formatDate(i.due_date)}</div>
                      <div className={`text-[11px] font-mono ${overdue ? "text-destructive" : "text-muted-foreground"}`}>
                        {overdue ? `${Math.abs(remaining)}d vencida` : remaining !== null ? `en ${remaining}d` : "—"}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </CardContent>
          </Card>
        </div>
      ))}

      {view === "empty" && items.length > 0 && (
        <Card>
          <CardContent className="p-0 divide-y divide-border">
            {items.map((i) => (
              <Link key={i.id} to={`/animales/${i.animal.id}`} data-testid={`predicted-birth-empty-row-${i.animal.id}`} className="p-3 flex items-center gap-3 text-sm hover:bg-accent/30 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="font-medium">Lomo {i.animal.lomo_number || "—"}</div>
                  <div className="text-xs text-muted-foreground">{farmName(i.animal.farm_id)} · Palpada {formatDate(i.palpation_date)}{i.registered_by_name && <> · por {i.registered_by_name}</>}</div>
                </div>
                <Badge variant={i.diagnosis === "empty_cull" ? "destructive" : "outline"}>{i.diagnosis === "empty_cull" ? "Vacía descarte" : "Vacía"}</Badge>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
